import { useEffect, useState } from "react";
import { getSummary } from "@/utils/pasUtils.js";

// One summary request per email per page load, shared by every caller.
const cache = new Map<string, Promise<string>>();

// Remembered across visits so the greeting renders in Arabic immediately.
const storageKey = (email: string) => `arabic-name:${email.toLowerCase()}`;

const readStored = (email: string): string => {
  if (!email) return "";
  try {
    return localStorage.getItem(storageKey(email)) || "";
  } catch {
    return "";
  }
};

const store = (email: string, name: string) => {
  try {
    localStorage.setItem(storageKey(email), name);
  } catch {
    // Storage unavailable - we'll just fetch again next time.
  }
};

const fetchArabicName = (email: string): Promise<string> => {
  if (!cache.has(email)) {
    const request = getSummary(
      import.meta.env.VITE_USERNAMET,
      import.meta.env.VITE_PASSWORD,
      email
    )
      .then((summary) => {
        const name = summary?.profile?.full_name_arabic?.trim() || "";
        if (name) store(email, name);
        return name;
      })
      .catch(() => {
        cache.delete(email); // allow a retry on the next mount
        return "";
      });
    cache.set(email, request);
  }
  return cache.get(email)!;
};

/**
 * The employee's Arabic full name from the HR profile.
 * `loading` is true only while there's no remembered name and the request is in flight.
 */
export const useArabicName = (email: string, enabled = true) => {
  const [name, setName] = useState(() => readStored(email));
  const [loading, setLoading] = useState(() => enabled && !readStored(email));

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (!email) return; // still waiting for the signed-in user

    const stored = readStored(email);
    setName(stored);
    setLoading(!stored);

    // Refresh in the background so a changed name is picked up.
    let cancelled = false;
    fetchArabicName(email).then((value) => {
      if (cancelled) return;
      if (value) setName(value);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [email, enabled]);

  return { name, loading };
};
